<script setup lang="ts">
/* The "Bookings" panel on a berth's or a vessel's page: loads its own rows
   for the filter it is given, so the detail pages do not each carry the same
   fetch, error and empty-state code. */
import { onMounted, ref, watch } from 'vue'
import Panel from '@/components/ui/Panel.vue'
import Alert from '@/components/ui/Alert.vue'
import BookingList from '@/components/BookingList.vue'
import { listBookings } from '@/lib/bookings'
import type { BookingFilter } from '@/lib/bookings'
import { errorMessage } from '@/lib/errors'
import type { Booking } from '@/lib/types'

const props = defineProps<{
  filter: BookingFilter
  description: string
  hide?: Array<'berth' | 'vessel'>
}>()

const emit = defineEmits<{ loaded: [count: number] }>()

const bookings = ref<Booking[]>([])
const loading = ref(true)
const error = ref('')

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    bookings.value = await listBookings(props.filter)
    emit('loaded', bookings.value.length)
  } catch (e) {
    error.value = errorMessage(e, 'Could not load bookings.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => JSON.stringify(props.filter), load)
</script>

<template>
  <Panel flush title="Bookings" :description="description">
    <Alert v-if="error" tone="danger" class="alert">{{ error }}</Alert>
    <p v-else-if="loading && bookings.length === 0" class="panel-status">Loading…</p>
    <p v-else-if="bookings.length === 0" class="panel-status">No bookings yet.</p>
    <BookingList v-else :bookings="bookings" :hide="hide" />
  </Panel>
</template>

<style scoped>
.alert {
  margin: var(--space-sm) var(--space-md);
}
</style>
